import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  MapPin, Globe, Clock, DollarSign, MessageSquare, Calendar, 
  ChevronLeft, Flame, Sparkles, Zap, ArrowRight 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DestinationDetail = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [destination, setDestination] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  const burnIcons = { 1: Sparkles, 2: Flame, 3: Zap };
  const burnColors = {
    1: 'from-purple-500 to-pink-500',
    2: 'from-orange-500 to-amber-500',
    3: 'from-red-500 to-orange-600'
  };
  const burnBgColors = {
    1: 'bg-purple-600 text-white',
    2: 'bg-orange-500 text-white',
    3: 'bg-red-600 text-white'
  };

  const regionColors = {
    north_central_america: 'from-emerald-500 to-teal-600',
    south_america: 'from-orange-500 to-amber-600',
    caribbean: 'from-cyan-500 to-blue-600'
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch destination
        const destResponse = await fetch(`${API_URL}/api/destinations/${id}?lang=${language}`);
        if (!destResponse.ok) throw new Error('Destination not found');
        const destData = await destResponse.json();
        setDestination(destData);

        // Fetch trips for this destination's region
        const tripsResponse = await fetch(`${API_URL}/api/trips?region=${destData.region}&lang=${language}`);
        const tripsData = await tripsResponse.json();
        setTrips(tripsData.trips || []);
      } catch (error) {
        console.error('Error fetching destination:', error);
        navigate('/destinations');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, language, navigate]);

  const getVibeEmoji = (vibe) => {
    const vibeEmojis = {
      adventure: '🏔️',
      culture: '🎭',
      relaxation: '🌴'
    };
    return vibeEmojis[vibe] || '✨';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-96 bg-gray-200 rounded-2xl"></div>
            <div className="h-12 bg-gray-200 rounded w-1/3"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!destination) return null;

  const regionGradient = regionColors[destination.region] || regionColors.north_central_america;

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid="destination-detail-page">
      {/* Hero Section */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img
          src={destination.image}
          alt={destination.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        
        {/* Back Button */}
        <div className="absolute top-8 left-4 sm:left-8">
          <Button
            onClick={() => navigate('/destinations')}
            variant="ghost"
            className="bg-white/10 backdrop-blur-sm text-white hover:bg-white/20"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            {language === 'es' ? 'Todos los Destinos' : 'All Destinations'}
          </Button>
        </div>

        {/* Destination Info on Hero */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            {/* Country Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${regionGradient} text-white text-sm font-bold mb-4`}>
              <Globe className="w-4 h-4" />
              {destination.country}
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4" data-testid="destination-name">
              {destination.name}
            </h1>

            {/* Vibes */}
            <div className="flex flex-wrap gap-3">
              {destination.vibes?.map((vibe, i) => (
                <span 
                  key={i} 
                  className="px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-medium"
                >
                  {getVibeEmoji(vibe)} {vibe.charAt(0).toUpperCase() + vibe.slice(1)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-2xl font-black text-gray-900 mb-4">
                {language === 'es' ? 'Sobre este destino' : 'About this destination'}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {destination.description}
              </p>
            </div>

            {/* Activities */}
            {destination.activities && destination.activities.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h2 className="text-2xl font-black text-gray-900 mb-4">
                  {language === 'es' ? 'Actividades' : 'Activities'}
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {destination.activities.map((activity, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${regionGradient} flex items-center justify-center`}>
                        <span className="text-white text-lg">✓</span>
                      </div>
                      <span className="text-gray-700 font-medium">{activity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trips in this Region */}
            {trips.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-black text-gray-900">
                    {language === 'es' ? 'Viajes en esta región' : 'Trips in this region'}
                  </h2>
                  <Link 
                    to={`/trips?region=${destination.region}`}
                    className="text-[#84bc00] font-bold text-sm hover:underline flex items-center gap-1"
                  >
                    {language === 'es' ? 'Ver todos' : 'View all'}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="space-y-4">
                  {trips.slice(0, 3).map((trip) => {
                    const BurnIcon = burnIcons[trip.burn_level] || Flame;
                    const burnGradient = burnColors[trip.burn_level] || burnColors[2];
                    const burnBg = burnBgColors[trip.burn_level] || burnBgColors[2];

                    return (
                      <div
                        key={trip.id}
                        data-testid={`trip-card-${trip.id}`}
                        className="flex gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer group"
                        onClick={() => navigate(`/trips/${trip.id}`)}
                      >
                        {/* Trip Image */}
                        <div className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={trip.image}
                            alt={trip.name}
                            className="w-full h-full object-cover"
                          />
                          <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded ${burnBg} text-[10px] font-bold flex items-center gap-0.5`}>
                            <BurnIcon className="w-2.5 h-2.5" />
                            {trip.burn_level}
                          </div>
                        </div>

                        {/* Trip Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 group-hover:text-[#84bc00] transition-colors truncate">
                            {trip.name}
                          </h3>
                          <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {trip.duration}
                            </span>
                            <span className="font-bold text-[#84bc00]">
                              ${trip.price?.toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                            {trip.burn_label} • {trip.vibes?.join(', ')}
                          </p>
                        </div>

                        {/* Arrow */}
                        <div className="flex items-center">
                          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#84bc00] group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-24">
              <h3 className="text-lg font-black text-gray-900 mb-4">
                {language === 'es' ? 'Información Rápida' : 'Quick Info'}
              </h3>

              {destination.logistics && (
                <div className="space-y-4">
                  {destination.logistics.currency && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold">
                          {language === 'es' ? 'Moneda' : 'Currency'}
                        </p>
                        <p className="text-gray-900 font-medium">{destination.logistics.currency}</p>
                      </div>
                    </div>
                  )}

                  {destination.logistics.language && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold">
                          {language === 'es' ? 'Idioma' : 'Language'}
                        </p>
                        <p className="text-gray-900 font-medium">{destination.logistics.language}</p>
                      </div>
                    </div>
                  )}

                  {destination.logistics.timezone && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold">
                          {language === 'es' ? 'Zona Horaria' : 'Timezone'}
                        </p>
                        <p className="text-gray-900 font-medium">{destination.logistics.timezone}</p>
                      </div>
                    </div>
                  )}

                  {destination.logistics.best_time && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold">
                          {language === 'es' ? 'Mejor Época' : 'Best Time'}
                        </p>
                        <p className="text-gray-900 font-medium">{destination.logistics.best_time}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* CTA */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <Button
                  onClick={() => navigate(`/trips?region=${destination.region}`)}
                  className={`w-full bg-gradient-to-r ${regionGradient} hover:opacity-90 text-white font-bold py-4 rounded-lg transition-all`}
                >
                  {language === 'es' ? 'Ver Viajes Disponibles' : 'View Available Trips'}
                </Button>
                <Button
                  onClick={() => navigate('/consultation')}
                  variant="outline"
                  className="w-full mt-3 border-2 border-gray-200 hover:border-[#84bc00] hover:text-[#84bc00] font-bold py-4 rounded-lg transition-all"
                >
                  {language === 'es' ? 'Planifica Tu Viaje' : 'Plan Your Trip'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DestinationDetail;
